import { useState } from 'react'
import { Sparkles } from 'lucide-react'
import { useSendMessage } from '@/features/messaging/useMessaging'
import { useCreateAiDraft, useDismissAiDraft } from '@/features/ai/useAiDrafts'
import { MESSAGE_TOKENS } from '@/features/messaging/tokens'
import { useDeal, useDealContacts } from '@/features/board/useBoard'
import { ApiError } from '@/api/client'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import MessageComposer from '@/features/messaging/MessageComposer'
import Card from '@/components/layout/Card'
import type { ComposerDraft } from '@/features/messaging/useComposerDraft'

/**
 * The deal's Send message composer with the AI assistant merged in: the "AI Assistant" toggle
 * drafts a follow-up (email/SMS) straight into the one unsent draft for the user to review and
 * send. Nothing leaves without the explicit Send; the generated suggestion is dismissed once its
 * text is pulled in. The draft is owned by the parent (lifted from {@code useComposerDraft}) so the
 * workspace header's Email/SMS quick actions can preset the channel.
 */
export default function DealComposer({
  dealId,
  draft,
  updateDraft,
}: {
  dealId: string
  draft: ComposerDraft
  updateDraft: (patch: Partial<ComposerDraft>) => void
}) {
  const { data: deal } = useDeal(dealId)
  const { data: dealContacts } = useDealContacts(dealId)
  const send = useSendMessage(dealId)
  const [sendError, setSendError] = useState<string | null>(null)

  const createDraft = useCreateAiDraft(dealId)
  const dismissDraft = useDismissAiDraft(dealId)
  const [showAi, setShowAi] = useState(false)
  const [aiInstruction, setAiInstruction] = useState('')
  const [aiError, setAiError] = useState<string | null>(null)

  const primary = deal?.primaryContactId
    ? dealContacts?.find((c) => c.contactId === deal.primaryContactId)
    : undefined
  const sampleContact = primary
    ? { firstName: primary.firstName, lastName: primary.lastName, email: primary.email }
    : undefined
  const sendTokens = MESSAGE_TOKENS.filter((t) => draft.channel === 'email' || t !== '{{agent_signature}}')

  function draftWithAi() {
    setAiError(null)
    createDraft.mutate(
      { channel: draft.channel === 'sms' ? 'sms' : 'email', instruction: aiInstruction.trim() || undefined },
      {
        onSuccess: (suggestion) => {
          updateDraft({ templateId: '', subject: suggestion.subject ?? '', body: suggestion.body })
          dismissDraft.mutate(suggestion.id)
          setAiInstruction('')
          setShowAi(false)
        },
        onError: (e) => setAiError(apiErrorMessage(e)),
      },
    )
  }

  function doSend({ includeSignature }: { includeSignature: boolean }) {
    if (!draft.templateId && !draft.body.trim()) return
    setSendError(null)
    send.mutate(
      {
        channel: draft.channel,
        templateId: draft.templateId || undefined,
        subject: draft.templateId || draft.channel !== 'email' ? undefined : draft.subject,
        body: draft.templateId ? undefined : draft.body,
        attachments: draft.channel === 'email' && draft.attachments.length
          ? draft.attachments.map((f) => ({ key: f.key, filename: f.filename, contentType: f.contentType }))
          : undefined,
        includeSignature,
      },
      {
        onSuccess: () => updateDraft({ body: '', subject: '', attachments: [] }),
        onError: (e) =>
          setSendError(
            e instanceof ApiError && e.code === 'consent.blocked'
              ? `Contact opted out of ${draft.channel === 'sms' ? 'SMS' : 'email'}.`
              : apiErrorMessage(e),
          ),
      },
    )
  }

  // The AI Assistant toggle lives in the card header (it just reveals the instruction field below).
  const aiToggle = (
    <button
      type="button"
      onClick={() => setShowAi((v) => !v)}
      className={`flex items-center gap-1 text-xs font-medium rounded-lg px-2 py-1 transition-colors ${
        showAi ? 'bg-brand-primary text-white' : 'text-brand-primary hover:bg-brand-primary/10'
      }`}
      title="Draft this message with AI"
    >
      <Sparkles size={13} /> AI Assistant
    </button>
  )

  return (
    <Card title="Send message" action={aiToggle}>
      {showAi && (
        <div className="mb-3 rounded-lg border border-brand-primary/20 bg-brand-primary/5 p-2.5">
          <input
            value={aiInstruction}
            onChange={(e) => setAiInstruction(e.target.value)}
            placeholder="Tell the assistant what to focus on…"
            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
          />
          <div className="flex items-center justify-between gap-2 mt-2">
            <p className="text-[10px] text-gray-400">
              Drafts the {draft.channel === 'sms' ? 'SMS' : 'email'} into the box below — review before sending.
            </p>
            <button
              type="button"
              onClick={draftWithAi}
              disabled={createDraft.isPending}
              className="shrink-0 px-3 py-1.5 bg-brand-primary text-white rounded-lg text-xs font-medium hover:bg-brand-dark disabled:opacity-60"
            >
              {createDraft.isPending ? 'Drafting…' : 'Draft with AI'}
            </button>
          </div>
          {aiError && <p className="text-danger text-xs mt-1">{aiError}</p>}
        </div>
      )}

      <MessageComposer
        channel={draft.channel}
        onChannelChange={(c) => {
          updateDraft({ channel: c })
          setSendError(null)
        }}
        templateId={draft.templateId}
        onTemplateChange={(t) => updateDraft({ templateId: t })}
        subject={draft.subject}
        onSubjectChange={(s) => updateDraft({ subject: s })}
        body={draft.body}
        onBodyChange={(b) => updateDraft({ body: b })}
        recipientCount={1}
        onSend={doSend}
        sending={send.isPending}
        sampleContact={sampleContact}
        tokens={sendTokens}
        sendLabel="Send"
        inline
        previewValues={{ deal_title: deal?.title ?? '' }}
        previewTarget={{ kind: 'deal', id: dealId }}
        customFields={['contact', 'deal']}
        allowAttachments
        attachments={draft.attachments}
        onAttachmentsChange={(a) => updateDraft({ attachments: a })}
      />
      {sendError && <p className="text-danger text-xs mt-1">{sendError}</p>}
    </Card>
  )
}
